import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "step",
    "spinner",
    "checkmark",
    "progressBar",
    "stepSubtext"
  ]

  connect() {
    this.steps = [
      { progressStart: 0, progressEnd: 30 },
      { progressStart: 30, progressEnd: 60 },
      { progressStart: 60, progressEnd: 95 }
    ]

    this.currentStepIndex = 0
    this.currentProgress = 0
    this.timeouts = []
    this.progressAnimationFrame = null

    this.startRitual()
    this.startProgressAnimation()
  }

  disconnect() {
    this.clearTimeouts()
    this.stopProgressAnimation()
  }

  clearTimeouts() {
    if (this.timeouts) {
      this.timeouts.forEach(t => clearTimeout(t))
    }
    this.timeouts = []
  }

  startRitual() {
    this.clearTimeouts()

    // T=3s: Step 1 Success, Step 2 Start
    this.scheduleStep(1, 5000)

    // T=6s: Step 2 Success, Step 3 Start
    this.scheduleStep(2, 10000)

    // T=11s: Show subtext on final step if still loading
    this.timeouts.push(setTimeout(() => {
      this.showFinalStepSubtext()
    }, 15000))
  }

  showFinalStepSubtext() {
    const finalStepSubtext = this.stepSubtextTargets[2]
    if (finalStepSubtext) {
      finalStepSubtext.textContent = "This one is tricky, hold on"
      finalStepSubtext.classList.remove('hidden')
    }
  }

  scheduleStep(stepIndex, delay) {
    this.timeouts.push(setTimeout(() => {
      this.transitionToStep(stepIndex)
    }, delay))
  }

  transitionToStep(stepIndex) {
    if (stepIndex <= 0 || stepIndex > 3) return

    // Previous step success
    this.markStepSuccess(stepIndex - 1)

    // Current step active
    if (stepIndex < 3) {
      this.activateStep(stepIndex)
    }

    // Update current step index for progress animation
    this.currentStepIndex = Math.min(stepIndex, 2)
  }

  markStepSuccess(index) {
    if (index >= this.steps.length) return

    const spinner = this.spinnerTargets[index]
    const checkmark = this.checkmarkTargets[index]

    spinner.classList.add('hidden')
    checkmark.classList.remove('hidden')
  }

  activateStep(index) {
    if (index >= this.steps.length) return
    const stepEl = this.stepTargets[index]
    stepEl.classList.remove('opacity-25')
    this.spinnerTargets[index].classList.remove('hidden')
  }

  startProgressAnimation() {
    this.progressStartTime = performance.now()
    this.animateProgress()
  }

  stopProgressAnimation() {
    if (this.progressAnimationFrame) {
      cancelAnimationFrame(this.progressAnimationFrame)
      this.progressAnimationFrame = null
    }
  }

  animateProgress() {
    const step = this.steps[this.currentStepIndex]
    const stepDuration = this.getStepDuration(this.currentStepIndex)
    const elapsed = performance.now() - this.progressStartTime

    // Calculate progress within current step using easeOutQuad for natural deceleration
    const rawProgress = Math.min(elapsed / stepDuration, 1)
    const easedProgress = this.easeOutQuad(rawProgress)

    // Interpolate between step's start and end progress
    const targetProgress = step.progressStart + (step.progressEnd - step.progressStart) * easedProgress

    // Smoothly animate toward target
    this.currentProgress += (targetProgress - this.currentProgress) * 0.1
    this.progressBarTarget.style.width = `${this.currentProgress}%`

    // Reset timer when step changes (detected by reaching near the end)
    if (rawProgress >= 1 && this.currentStepIndex < 2) {
      this.progressStartTime = performance.now()
    }

    this.progressAnimationFrame = requestAnimationFrame(() => this.animateProgress())
  }

  getStepDuration(stepIndex) {
    // Match the ritual timing: step 0 = 3s, step 1 = 3s, step 2 = 4s
    const durations = [5000, 10000, 15000]
    return durations[stepIndex] || 5000
  }

  easeOutQuad(t) {
    return t * (2 - t)
  }
}
